import { useEffect } from "react";
import { useGetBusinessViaIdentifier } from "@/lib/apollo/hooks/booking-edge";
import BranchSelector from "@/components/booking/BranchSelector";
import { usePreserveQueryParams } from "@/hooks/usePreserveQueryParams";

export default function BusinessPageClient({
  businessId,
}: {
  businessId: string;
}) {
  const { navigateWithParams } = usePreserveQueryParams();
  const { data, loading, error } = useGetBusinessViaIdentifier(businessId);

  const business = data?.business;
  const salons = business?.salons || [];

  useEffect(() => {
    // Always redirect to the first salon automatically, preserving query params (e.g., ?source=marketplace)
    if (salons.length > 0 && salons[0].urlIdentifier) {
      navigateWithParams(`/${businessId}/${salons[0].urlIdentifier}`, { replace: true });
    }
  }, [salons, businessId, navigateWithParams]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-grey-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-300 mx-auto"></div>
          <p className="mt-4 text-grey-600">Loading locations...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-grey-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-2">Error loading business</p>
          <p className="text-grey-600 text-sm">{error.message}</p>
        </div>
      </div>
    );
  }

  // No business found
  if (!business) {
    return (
      <div className="min-h-screen bg-grey-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-grey-900 text-xl mb-2">Business not found</p>
          <p className="text-grey-600">Please check the URL and try again</p>
        </div>
      </div>
    );
  }

  // Auto-redirecting to first salon
  if (salons.length > 0) {
    return (
      <div className="min-h-screen bg-grey-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-300 mx-auto"></div>
          <p className="mt-4 text-grey-600">Redirecting...</p>
        </div>
      </div>
    );
  }

  // No salons available
  return (
    <div className="min-h-screen bg-grey-50 flex items-center justify-center">
      <div className="text-center">
        <p className="text-grey-900 text-xl mb-2">No locations available</p>
        <p className="text-grey-600">This business has no active locations</p>
      </div>
    </div>
  );
}